import React, { useState, useEffect } from "react";
import { useForm, Controller, FieldPath } from "react-hook-form";
import {
	TextField,
	Grid,
	Button,
	Box,
	Select,
	MenuItem,
	IconButton,
	Checkbox,
	FormControlLabel,
	Typography,
	Chip,
} from "@mui/material";
import Modal from "./Modal";
import { useAppTheme } from "../../../utils/theme";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
	RewardConfigurationType,
	RewardsEngine,
	CustomLadderReset,
	EventResultCondition,
} from "../../../domain/reward.interface";

export interface RewardsConfigFormData {
	eventType: string;
	event: string;
	rewardConfigurationType: RewardConfigurationType;
	fixedLadderConfiguration?: { index: number; points: number }[];
	programmableLadderConfiguration?: {
		startValue: number;
		stepValue: number;
		maxValue: number;
		resetValue: number;
	};
	maxOccurrences?: number;
	rewardBasedOnResult?: boolean;
	validValue?: string;
	invalidValue?: string;
	invalidResult?: {
		invalidResultType: string;
		rewardForInvalid: string;
		resetRuleType: string;
		specificIndex: number;
	};
	customLadderReset?: CustomLadderReset;
	createdBy: string;
}

const defaultValues: RewardsConfigFormData = {
	eventType: "",
	event: "",
	rewardConfigurationType: RewardConfigurationType.FixedLadder,
	fixedLadderConfiguration: [{ index: 1, points: 0 }],
	programmableLadderConfiguration: {
		startValue: 0,
		stepValue: 1,
		maxValue: 5,
		resetValue: 0,
	},
	maxOccurrences: 3,
	rewardBasedOnResult: false,
	customLadderReset: {
		enabled: false,
		conditions: [],
		validResultIds: [],
		invalidResultIds: [],
		recovery: {
			enabled: false,
		},
	},
	createdBy: "",
};

interface RewardsConfigModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (data: RewardsConfigFormData) => void;
	isPending?: boolean;
	mode?: "create" | "edit" | "view";
	initialData?: RewardsEngine;
}

const RewardsConfigModal: React.FC<RewardsConfigModalProps> = ({
	open,
	onClose,
	onSave,
	isPending = false,
	mode = "create",
	initialData,
}) => {
	const theme = useAppTheme();
	const isViewMode = mode === "view";

	const { handleSubmit, control, watch, setValue, reset } =
		useForm<RewardsConfigFormData>({
			defaultValues,
		});

	const rewardConfigurationType = watch("rewardConfigurationType");
	const rewardBasedOnResult = watch("rewardBasedOnResult");
	const maxOccurrences =
		watch("maxOccurrences") || defaultValues.maxOccurrences;
	const fixedLadderConfiguration = watch("fixedLadderConfiguration");

	const [currentSteps, setCurrentSteps] = useState(
		defaultValues.fixedLadderConfiguration?.length || 1,
	);
	const [quickFillSteps, setQuickFillSteps] = useState<number>(10);
	const [quickFillValue, setQuickFillValue] = useState<number>(0);

	// Watch customLadderReset fields
	const customLadderResetEnabled = watch("customLadderReset.enabled");
	const customLadderResetConditions =
		watch("customLadderReset.conditions") || [];
	const recoveryEnabled = watch("customLadderReset.recovery.enabled");

	// Populate form when initialData changes (for edit/view modes)
	useEffect(() => {
		if (initialData && open) {
			reset({
				eventType: initialData.eventType || "",
				event: initialData.event || "",
				rewardConfigurationType:
					initialData.rewardConfigurationType ||
					RewardConfigurationType.FixedLadder,
				fixedLadderConfiguration: initialData.fixedLadderConfiguration || [],
				programmableLadderConfiguration:
					initialData.programmableLadderConfiguration?.[0],
				maxOccurrences: initialData.maxOccurrences || 99999,
				rewardBasedOnResult: initialData.rewardBasedOnResult || false,
				validValue: initialData.validValue,
				invalidValue: initialData.invalidValue,
				invalidResult: initialData.invalidResult,
				customLadderReset:
					initialData.customLadderReset || defaultValues.customLadderReset,
				createdBy: initialData.createdBy || "",
			});

			if (initialData.fixedLadderConfiguration) {
				setCurrentSteps(initialData.fixedLadderConfiguration.length);
			}
		} else if (!open) {
			// Reset form when modal closes
			reset(defaultValues);
			setCurrentSteps(1);
		}
	}, [initialData, open, reset]);

	const getModalTitle = () => {
		switch (mode) {
			case "view":
				return "View Rewards Configuration";
			case "edit":
				return "Edit Rewards Configuration";
			default:
				return "Create Rewards Configuration";
		}
	};

	const onSubmit = (data: RewardsConfigFormData) => {
		if (!isViewMode) {
			onSave(data);
		}
	};

	const handleAddStep = () => {
		setCurrentSteps((prev) => prev + 1);
		setValue(`fixedLadderConfiguration.${currentSteps}`, {
			index: currentSteps + 1,
			points: 0,
		});
	};

	const handleRemoveStep = (stepIndex: number) => {
		setValue(
			"fixedLadderConfiguration",
			fixedLadderConfiguration?.filter((_, index) => index !== stepIndex),
		);
		setCurrentSteps((prev) => prev - 1);
	};

	const handleQuickFill = () => {
		if (quickFillSteps <= 0 || quickFillSteps > (maxOccurrences || 99999)) {
			return;
		}

		const currentConfig = fixedLadderConfiguration || [];
		const newSteps = [];

		// Add the specified number of steps with the same value
		for (let i = 0; i < quickFillSteps; i++) {
			newSteps.push({
				index: currentSteps + i + 1,
				points: quickFillValue,
			});
		}

		// Append new steps to existing ones
		const updatedConfig = [...currentConfig, ...newSteps];
		setValue("fixedLadderConfiguration", updatedConfig);
		setCurrentSteps(updatedConfig.length);
	};

	// Custom Ladder Reset handlers
	const handleAddCondition = () => {
		const currentConditions = customLadderResetConditions;
		const newCondition: EventResultCondition = {
			relativePosition: -1,
			checkType: "IS_INVALID",
			description: "",
		};
		setValue("customLadderReset.conditions", [
			...currentConditions,
			newCondition,
		]);
	};

	const handleRemoveCondition = (index: number) => {
		const currentConditions = customLadderResetConditions;
		setValue(
			"customLadderReset.conditions",
			currentConditions.filter((_, i) => i !== index),
		);
	};

	const handleAddValidResultId = (value: string) => {
		if (!value.trim()) return;
		const current = watch("customLadderReset.validResultIds") || [];
		setValue("customLadderReset.validResultIds", [...current, value.trim()]);
	};

	const handleRemoveValidResultId = (index: number) => {
		const current = watch("customLadderReset.validResultIds") || [];
		setValue(
			"customLadderReset.validResultIds",
			current.filter((_, i) => i !== index),
		);
	};

	const handleAddInvalidResultId = (value: string) => {
		if (!value.trim()) return;
		const current = watch("customLadderReset.invalidResultIds") || [];
		setValue("customLadderReset.invalidResultIds", [...current, value.trim()]);
	};

	const handleRemoveInvalidResultId = (index: number) => {
		const current = watch("customLadderReset.invalidResultIds") || [];
		setValue(
			"customLadderReset.invalidResultIds",
			current.filter((_, i) => i !== index),
		);
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			isPending={isPending}
			title={getModalTitle()}
			theme={theme}
			size={{
				width: "100%",
				minHeight: 640,
				maxHeight: 720,
				maxWidth: 760,
			}}
			content={
				<Box>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid container spacing={2}>
							<Grid item xs={12} mt={2}>
								<Controller
									name="eventType"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Event Type"
											fullWidth
											variant="outlined"
											required
											disabled={isViewMode}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="event"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Event"
											fullWidth
											variant="outlined"
											required
											disabled={isViewMode}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="rewardConfigurationType"
									control={control}
									render={({ field }) => (
										<Select
											{...field}
											fullWidth
											variant="outlined"
											label="Reward Configuration Type"
											defaultValue="FIXED_LADDER"
										>
											<MenuItem value="FIXED_LADDER">Fixed Ladder</MenuItem>
											<MenuItem value="PROGRAMMABLE_LADDER">
												Programmable Ladder
											</MenuItem>
										</Select>
									)}
								/>
							</Grid>
							{rewardConfigurationType === "FIXED_LADDER" && (
								<>
									<Grid item xs={12}>
										<Controller
											name="maxOccurrences"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Max Occurrences"
													fullWidth
													variant="outlined"
													type="number"
													inputProps={{ min: 1, max: 10 }}
													disabled={isViewMode}
												/>
											)}
										/>
									</Grid>

									{/* Quick Fill Section */}
									{!isViewMode && (
										<Grid item xs={12}>
											<Box
												sx={{
													border: "1px dashed",
													borderColor: theme.palette.primary.main,
													borderRadius: 1,
													p: 2,
													bgcolor: theme.palette.action.hover,
												}}
											>
												<Grid container spacing={2} alignItems="center">
													<Grid item xs={12}>
														<Typography variant="subtitle2" color="primary">
															Quick Fill Steps
														</Typography>
													</Grid>
													<Grid item xs={4}>
														<TextField
															label="Number of Steps"
															fullWidth
															variant="outlined"
															type="number"
															value={quickFillSteps}
															onChange={(e) =>
																setQuickFillSteps(Number(e.target.value))
															}
															inputProps={{ min: 1 }}
															size="small"
														/>
													</Grid>
													<Grid item xs={4}>
														<TextField
															label="Points Value"
															fullWidth
															variant="outlined"
															type="number"
															value={quickFillValue}
															onChange={(e) =>
																setQuickFillValue(Number(e.target.value))
															}
															size="small"
														/>
													</Grid>
													<Grid item xs={4}>
														<Button
															variant="contained"
															color="primary"
															onClick={handleQuickFill}
															fullWidth
															disabled={
																currentSteps >= (maxOccurrences || 99999) ||
																quickFillSteps <= 0
															}
														>
															Add {quickFillSteps} Steps
														</Button>
													</Grid>
												</Grid>
											</Box>
										</Grid>
									)}

									{fixedLadderConfiguration?.map((_, index) => (
										<React.Fragment key={index}>
											<Grid item xs={5}>
												<Controller
													name={`fixedLadderConfiguration.${index}.index`}
													control={control}
													render={({ field }) => (
														<TextField
															{...field}
															label={`Ladder Index ${index + 1}`}
															fullWidth
															variant="outlined"
															type="number"
															InputProps={{ readOnly: true }}
														/>
													)}
												/>
											</Grid>
											<Grid item xs={5}>
												<Controller
													name={`fixedLadderConfiguration.${index}.points`}
													control={control}
													render={({ field }) => (
														<TextField
															{...field}
															label="Points"
															fullWidth
															variant="outlined"
															type="number"
															disabled={isViewMode}
														/>
													)}
												/>
											</Grid>
											<Grid item xs={2}>
												{index > 0 && !isViewMode && (
													<IconButton
														onClick={() => handleRemoveStep(index)}
														color="error"
														aria-label="remove step"
													>
														<RemoveCircleIcon />
													</IconButton>
												)}
											</Grid>
										</React.Fragment>
									))}
									{!isViewMode && currentSteps < (maxOccurrences || 1) && (
										<Grid item xs={12}>
											<Button
												variant="outlined"
												color="primary"
												onClick={handleAddStep}
												disabled={currentSteps >= (maxOccurrences || 1)}
											>
												Add Step
											</Button>
										</Grid>
									)}
								</>
							)}

							{rewardConfigurationType === "PROGRAMMABLE_LADDER" && (
								<>
									<Grid item xs={3}>
										<Controller
											name="programmableLadderConfiguration.startValue"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Start Value"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={3}>
										<Controller
											name="programmableLadderConfiguration.stepValue"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Step Value"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={3}>
										<Controller
											name="programmableLadderConfiguration.maxValue"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Max Value"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={3}>
										<Controller
											name="programmableLadderConfiguration.resetValue"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Reset Value"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
								</>
							)}

							<Grid item xs={12}>
								<Controller
									name="rewardBasedOnResult"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													{...field}
													checked={field.value}
													onChange={(e) =>
														setValue("rewardBasedOnResult", e.target.checked)
													}
												/>
											}
											label="Reward Based on Result"
										/>
									)}
								/>
							</Grid>

							{rewardBasedOnResult && (
								<>
									<Grid item xs={6}>
										<Controller
											name="validValue"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Valid Value"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name="invalidValue"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Invalid Value"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>

									<Grid item xs={6}>
										<Controller
											name="invalidResult.invalidResultType"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Invalid Result Type"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name="invalidResult.rewardForInvalid"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Reward for Invalid"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name="invalidResult.resetRuleType"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Reset Rule Type"
													fullWidth
													variant="outlined"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name="invalidResult.specificIndex"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Specific Index"
													fullWidth
													variant="outlined"
													type="number"
												/>
											)}
										/>
									</Grid>
								</>
							)}

							{/* Custom Ladder Reset Configuration */}
							<Grid item xs={12}>
								<Controller
									name="customLadderReset.enabled"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													{...field}
													checked={field.value || false}
													disabled={isViewMode}
												/>
											}
											label="Enable Custom Ladder Reset"
										/>
									)}
								/>
							</Grid>

							{customLadderResetEnabled && (
								<>
									<Grid item xs={12}>
										<Typography variant="h6" gutterBottom>
											Custom Ladder Reset Configuration
										</Typography>
									</Grid>

									{/* Valid Result IDs */}
									<Grid item xs={12}>
										<Typography variant="subtitle2" gutterBottom>
											Valid Result IDs
										</Typography>
										<Box
											sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
										>
											{(watch("customLadderReset.validResultIds") || []).map(
												(id, index) => (
													<Chip
														key={index}
														label={id}
														onDelete={
															!isViewMode
																? () => handleRemoveValidResultId(index)
																: undefined
														}
													/>
												),
											)}
										</Box>
										{!isViewMode && (
											<TextField
												placeholder="Add valid result ID and press Enter"
												fullWidth
												variant="outlined"
												size="small"
												onKeyPress={(e) => {
													if (e.key === "Enter") {
														e.preventDefault();
														const input = e.target as HTMLInputElement;
														handleAddValidResultId(input.value);
														input.value = "";
													}
												}}
											/>
										)}
									</Grid>

									{/* Invalid Result IDs */}
									<Grid item xs={12}>
										<Typography variant="subtitle2" gutterBottom>
											Invalid Result IDs
										</Typography>
										<Box
											sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
										>
											{(watch("customLadderReset.invalidResultIds") || []).map(
												(id, index) => (
													<Chip
														key={index}
														label={id}
														onDelete={
															!isViewMode
																? () => handleRemoveInvalidResultId(index)
																: undefined
														}
													/>
												),
											)}
										</Box>
										{!isViewMode && (
											<TextField
												placeholder="Add invalid result ID and press Enter"
												fullWidth
												variant="outlined"
												size="small"
												onKeyPress={(e) => {
													if (e.key === "Enter") {
														e.preventDefault();
														const input = e.target as HTMLInputElement;
														handleAddInvalidResultId(input.value);
														input.value = "";
													}
												}}
											/>
										)}
									</Grid>

									{/* Conditions */}
									<Grid item xs={12}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
											}}
										>
											<Typography variant="subtitle2">Conditions</Typography>
											{!isViewMode && (
												<Button
													size="small"
													startIcon={<AddCircleOutlineIcon />}
													onClick={handleAddCondition}
												>
													Add Condition
												</Button>
											)}
										</Box>
									</Grid>

									{customLadderResetConditions.map((condition, index) => (
										<Grid item xs={12} key={index}>
											<Box
												sx={{
													border: "1px solid #e0e0e0",
													borderRadius: 1,
													p: 2,
													position: "relative",
												}}
											>
												{!isViewMode && (
													<IconButton
														size="small"
														sx={{ position: "absolute", top: 8, right: 8 }}
														onClick={() => handleRemoveCondition(index)}
													>
														<RemoveCircleOutlineIcon />
													</IconButton>
												)}
												<Grid container spacing={2}>
													<Grid item xs={6}>
														<Controller
															name={
																`customLadderReset.conditions.${index}.relativePosition` as FieldPath<RewardsConfigFormData>
															}
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	label="Relative Position"
																	fullWidth
																	variant="outlined"
																	type="number"
																	disabled={isViewMode}
																	helperText="Negative for past results (e.g., -1 for previous)"
																/>
															)}
														/>
													</Grid>
													<Grid item xs={6}>
														<Controller
															name={
																`customLadderReset.conditions.${index}.checkType` as FieldPath<RewardsConfigFormData>
															}
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	select
																	label="Check Type"
																	fullWidth
																	variant="outlined"
																	disabled={isViewMode}
																>
																	<MenuItem value="IS_INVALID">
																		Is Invalid
																	</MenuItem>
																	<MenuItem value="IS_VALID">Is Valid</MenuItem>
																	<MenuItem value="HAS_SPECIFIC_ID">
																		Has Specific ID
																	</MenuItem>
																</TextField>
															)}
														/>
													</Grid>
													<Grid item xs={6}>
														<Controller
															name={
																`customLadderReset.conditions.${index}.action` as FieldPath<RewardsConfigFormData>
															}
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	select
																	label="Action"
																	fullWidth
																	variant="outlined"
																	disabled={isViewMode}
																>
																	<MenuItem value="RESET_TO_STEP">
																		Reset to Step
																	</MenuItem>
																	<MenuItem value="DECREASE_BY">
																		Decrease By
																	</MenuItem>
																	<MenuItem value="INCREASE_BY">
																		Increase By
																	</MenuItem>
																	<MenuItem value="PAY_ZERO">Pay Zero</MenuItem>
																</TextField>
															)}
														/>
													</Grid>
													<Grid item xs={6}>
														<Controller
															name={
																`customLadderReset.conditions.${index}.targetStep` as FieldPath<RewardsConfigFormData>
															}
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	label="Target Step (if applicable)"
																	fullWidth
																	variant="outlined"
																	type="number"
																	disabled={isViewMode}
																/>
															)}
														/>
													</Grid>
													<Grid item xs={6}>
														<Controller
															name={
																`customLadderReset.conditions.${index}.customAmount` as FieldPath<RewardsConfigFormData>
															}
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	label="Custom Amount (if applicable)"
																	fullWidth
																	variant="outlined"
																	type="number"
																	disabled={isViewMode}
																/>
															)}
														/>
													</Grid>
													<Grid item xs={6}>
														<Controller
															name={
																`customLadderReset.conditions.${index}.specificResultId` as FieldPath<RewardsConfigFormData>
															}
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	label="Specific Result ID (if applicable)"
																	fullWidth
																	variant="outlined"
																	disabled={isViewMode}
																/>
															)}
														/>
													</Grid>
													<Grid item xs={12}>
														<Controller
															name={
																`customLadderReset.conditions.${index}.description` as FieldPath<RewardsConfigFormData>
															}
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	label="Description"
																	fullWidth
																	variant="outlined"
																	multiline
																	rows={2}
																	disabled={isViewMode}
																/>
															)}
														/>
													</Grid>
												</Grid>
											</Box>
										</Grid>
									))}

									{/* Recovery Configuration */}
									<Grid item xs={12}>
										<Controller
											name="customLadderReset.recovery.enabled"
											control={control}
											render={({ field }) => (
												<FormControlLabel
													control={
														<Checkbox
															{...field}
															checked={field.value || false}
															disabled={isViewMode}
														/>
													}
													label="Enable Recovery"
												/>
											)}
										/>
									</Grid>

									{recoveryEnabled && (
										<>
											<Grid item xs={12}>
												<Typography variant="subtitle2" gutterBottom>
													Recovery Configuration
												</Typography>
											</Grid>
											<Grid item xs={6}>
												<Controller
													name="customLadderReset.recovery.type"
													control={control}
													render={({ field }) => (
														<TextField
															{...field}
															select
															label="Recovery Type"
															fullWidth
															variant="outlined"
															disabled={isViewMode}
														>
															<MenuItem value="PUSH_STEP">Push Step</MenuItem>
															<MenuItem value="REPLACE_STEP">
																Replace Step
															</MenuItem>
														</TextField>
													)}
												/>
											</Grid>
											<Grid item xs={6}>
												<Controller
													name="customLadderReset.recovery.resetToStep"
													control={control}
													render={({ field }) => (
														<TextField
															{...field}
															label="Reset to Step (default: 1)"
															fullWidth
															variant="outlined"
															type="number"
															disabled={isViewMode}
															helperText="Which step to reset to after trigger"
														/>
													)}
												/>
											</Grid>
											<Grid item xs={6}>
												<Controller
													name="customLadderReset.recovery.recoverInSteps"
													control={control}
													render={({ field }) => (
														<TextField
															{...field}
															label="Recover in Steps (PUSH_STEP only)"
															fullWidth
															variant="outlined"
															type="number"
															disabled={isViewMode}
															helperText="How many visits to catch back up"
														/>
													)}
												/>
											</Grid>
											<Grid item xs={12}>
												<Controller
													name="customLadderReset.recovery.description"
													control={control}
													render={({ field }) => (
														<TextField
															{...field}
															label="Recovery Description"
															fullWidth
															variant="outlined"
															multiline
															rows={2}
															disabled={isViewMode}
														/>
													)}
												/>
											</Grid>
										</>
									)}

									{/* Max History Check */}
									<Grid item xs={6}>
										<Controller
											name="customLadderReset.maxHistoryCheck"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Max History Check (optional)"
													fullWidth
													variant="outlined"
													type="number"
													disabled={isViewMode}
												/>
											)}
										/>
									</Grid>
								</>
							)}
						</Grid>
					</form>
				</Box>
			}
			actions={
				<Box sx={{ display: "flex", gap: "10px" }}>
					<Button onClick={onClose} variant="outlined">
						{isViewMode ? "Close" : "Cancel"}
					</Button>
					{!isViewMode && (
						<Button onClick={handleSubmit(onSubmit)} variant="contained">
							{mode === "edit" ? "Update" : "Create"}
						</Button>
					)}
				</Box>
			}
		/>
	);
};

export default RewardsConfigModal;
